import { API_BASE } from './api-base';
import { getStripe, HandleNextActionResult, StripeInstance } from './stripe/embedded';

/**
 * The browser half of an SCA (3DS) upgrade — ADR-037's hold-until-paid mode.
 *
 * When SwitchPlan answers `UPGRADE_REQUIRES_ACTION`, the tier change is HELD in Stripe behind a
 * PaymentIntent the buyer must authenticate. `confirmHeldUpgrade` runs that challenge
 * (`stripe.handleNextAction`) and reduces the outcome to the three verdicts the picker can act on:
 *
 * - `paid` — the payment went through (or is settling); the paid webhook applies the tier and
 *   grants credits, so the picker polls premium exactly as it does for a synchronous upgrade.
 * - `not_completed` — DEFINITIVE: the challenge was failed or abandoned and Stripe left the
 *   payment unfinished. Nothing was charged and the tier did not change. The picker explicitly
 *   abandons the held upgrade (POST /api/premium/cancel-pending-upgrade) so the held invoice is
 *   voided now rather than left open for Stripe's ~23h expiry.
 * - `ambiguous` — the challenge's outcome is UNKNOWN (transport loss, an unexpected shape). The
 *   payment may already have succeeded, so this must never render as "payment failed" and must
 *   never abandon the hold — the picker says "finishing your upgrade", keeps the action's
 *   idempotency key, and polls; an exact-key retry or the paid webhook converges it.
 */
export type NextActionVerdict = 'paid' | 'not_completed' | 'ambiguous';
export type BillingLane = 'live' | 'sandbox';

/**
 * Where a held upgrade got to. Reported as BI so the outcome of a challenge is OBSERVABLE — before
 * this, an `ambiguous` resolution logged nothing anywhere: `reportError` only fires on a thrown
 * server error, and ambiguous deliberately neither abandons the hold nor surfaces a failure. Five
 * live buyers (2026-08-10/12, all US/CA) opened a hold and none completed, and no telemetry existed
 * to say whether they ever SAW a bank challenge. That is the question these stages answer.
 *
 * **The discriminator is one-directional, and must be read that way.** `challenge-invoked` is emitted
 * immediately before `handleNextAction`, so:
 *
 * - **absent ⇒ no challenge, definitively.** We never called Stripe, so nothing can have been drawn.
 *   `no-confirmable-response` (our own payload) and `stripe-unavailable` (the buyer's browser) say
 *   which, because the fix differs.
 * - **present ⇏ the buyer saw anything.** Stripe validates the client secret and the intent's own
 *   status inside that call and can reject before rendering any UI — an already-succeeded intent, a
 *   malformed secret, a wrong-key mismatch. Stripe.js exposes no "the modal was displayed" signal, so
 *   no event here can honestly assert one.
 *
 * To judge whether a challenge was really drawn, read the FOLLOW-UP stage: a `challenge-status`, or a
 * `challenge-error` whose code is authentication-related (e.g. `payment_intent_authentication_failure`),
 * means the SDK got as far as running it. A `challenge-threw`, or a `challenge-error` carrying a
 * resource/parameter code, means it very likely never rendered.
 */
export type ScaStage =
  /** resolveHeldUpgrade refused before touching Stripe — no secret, no key, or an unknown lane. */
  | 'no-confirmable-response'
  /** Stripe.js never loaded, or the response carried no publishable key. */
  | 'stripe-unavailable'
  /** handleNextAction was CALLED. Proves the invocation, NOT that any UI was shown — see above. */
  | 'challenge-invoked'
  /** handleNextAction resolved with an error; detail is `<type>/<code>`. */
  | 'challenge-error'
  /** handleNextAction resolved cleanly; detail is the PaymentIntent status. */
  | 'challenge-status'
  /** handleNextAction threw — the outcome is lost from that call, not from Stripe. */
  | 'challenge-threw'
  /** The intent was re-read to settle an inconclusive result; detail is the status it reported. */
  | 'intent-reread'
  /** The final verdict; detail is `paid` | `not_completed` | `ambiguous`. */
  | 'verdict'
  /** A definitive failure was reported to the server; detail says whether it confirmed the void. */
  | 'abandon';

/**
 * Telemetry sink. Deliberately fire-and-forget and never awaited: reporting must not be able to
 * change a money verdict, so a throwing reporter is swallowed at the call site.
 */
export type ScaStageReporter = (stage: ScaStage, detail?: string) => void;

/** Never let instrumentation break the flow it is measuring. */
function report(onStage: ScaStageReporter | undefined, stage: ScaStage, detail?: string): void {
  try {
    onStage?.(stage, detail);
  } catch {
    /* telemetry is best-effort by construction */
  }
}

// Only `succeeded` is a paid verdict. `processing` may still fail and `requires_capture` has not
// captured the funds yet; both therefore remain ambiguous so the caller keeps the action key and
// waits for the paid webhook to make the target plan observable.
const PAID_STATUSES = new Set(['succeeded']);
// Statuses that mean the challenge definitively did NOT complete the payment: a failed/dismissed
// 3DS lands the intent back on requires_payment_method (Stripe's documented failure state), and a
// canceled intent is dead. Deliberately NOT here: `requires_action` — an error-free resolution that
// still reports it is anomalous (a second pending action, the SDK racing the issuer), not Stripe's
// failure state, and the issuer's answer may still be on its way. Classifying it as a definitive
// failure would void a held invoice a moment before its payment completes; it falls through to
// `ambiguous` with every other unrecognized shape.
const NOT_COMPLETED_STATUSES = new Set(['requires_payment_method', 'canceled']);

// Error CODES that name the outcome outright, whatever `type` they arrive under. A failed 3DS was
// assumed to surface as `card_error`, but a real one (verified 2026-08-10, pi_3U2uPt…, IE/EUR
// sandbox) recorded `type: invalid_request_error` alongside this code, so a type-only check read a
// definitive failure as ambiguous and stranded the hold. The code is unambiguous — Stripe uses it
// only when authentication itself did not succeed.
const NOT_COMPLETED_CODES = new Set(['payment_intent_authentication_failure']);

/**
 * Reduce a `handleNextAction` result to a verdict. Pure — this is the money-relevant decision
 * (which failures may rotate the action key, which must keep it), so it is kept testable.
 */
export function classifyNextActionResult(result: HandleNextActionResult): NextActionVerdict {
  if (result.error) {
    // `card_error` / `validation_error` are Stripe's DEFINITIVE verdicts on the challenge itself
    // (authentication failed, abandoned, card refused). Everything else — api_connection_error,
    // api_error, rate_limit, an absent type — leaves the true outcome unknown.
    const type = result.error.type ?? '';
    if (type === 'card_error' || type === 'validation_error') return 'not_completed';
    if (NOT_COMPLETED_CODES.has(result.error.code ?? '')) return 'not_completed';
    // An error may still carry the intent it failed on; its status is authoritative either way.
    return classifyIntentStatus(result.error.payment_intent?.status ?? '');
  }
  return classifyIntentStatus(result.paymentIntent?.status ?? '');
}

function classifyIntentStatus(status: string): NextActionVerdict {
  if (PAID_STATUSES.has(status)) return 'paid';
  if (NOT_COMPLETED_STATUSES.has(status)) return 'not_completed';
  return 'ambiguous';
}

/**
 * Run the SCA challenge for a held upgrade's client secret and return the verdict.
 *
 * The publishable key is returned by the same switch response that created the hold. A failure to
 * even reach the challenge (Stripe.js blocked, the bound publishable key missing) is
 * `not_completed` rather than ambiguous: `handleNextAction` was never called, so no payment can
 * have been initiated by it — the held upgrade is still safely abandonable.
 */
export async function confirmHeldUpgrade(
  clientSecret: string,
  publishableKey: string,
  onStage?: ScaStageReporter,
): Promise<NextActionVerdict> {
  let stripe: StripeInstance;
  try {
    if (!publishableKey) {
      report(onStage, 'stripe-unavailable', 'no-publishable-key');
      return 'not_completed';
    }
    stripe = await getStripe(publishableKey);
  } catch {
    report(onStage, 'stripe-unavailable', 'load-failed');
    return 'not_completed';
  }
  try {
    // We CALLED handleNextAction. Deliberately not "shown": Stripe validates the secret and the
    // intent's own status first and can reject before rendering anything, so this proves the
    // invocation and nothing more. See ScaStage for which follow-up stages imply real UI.
    report(onStage, 'challenge-invoked');
    const result = await stripe.handleNextAction({ clientSecret });
    if (result.error) {
      report(onStage, 'challenge-error', `${result.error.type ?? 'none'}/${result.error.code ?? 'none'}`);
    } else {
      report(onStage, 'challenge-status', result.paymentIntent?.status ?? 'none');
    }
    const verdict = classifyNextActionResult(result);
    // Only an INCONCLUSIVE result is worth a second question; paid/not_completed are already
    // definitive and re-reading could only race them.
    return verdict === 'ambiguous' ? await verdictFromRetrievedIntent(stripe, clientSecret, onStage) : verdict;
  } catch {
    // The call was made and its outcome was lost from THIS call — but the PaymentIntent still knows.
    // A throw can also mean the SDK rejected the secret outright, i.e. no challenge was ever drawn;
    // the two are indistinguishable from here, which is why `challenge-invoked` claims neither.
    report(onStage, 'challenge-threw');
    return await verdictFromRetrievedIntent(stripe, clientSecret, onStage);
  }
}

// The settling read runs on the path that is REACHED BY transport failure, inside a modal the picker
// keeps non-dismissible while processing, so an unbounded wait renders as a hung checkout. Past this
// budget we stop waiting and answer `ambiguous` — exactly what the caller answered before this read
// existed, so timing out can only cost the narrowing, never a verdict.
const RETRIEVE_TIMEOUT_MS = 8_000;

/**
 * Ask Stripe for the intent's own status when the challenge's return value didn't settle it.
 *
 * The PaymentIntent is the authoritative record of what happened, and reading it is side-effect
 * free, so consulting it can only narrow `ambiguous` toward the truth — never invent a failure.
 * `requires_payment_method` (a failed/dismissed 3DS) and `canceled` become definitive; `processing`,
 * `requires_action` and anything unrecognized stay ambiguous exactly as before. If the read itself
 * fails, errors, or outruns its budget we are no worse off than before it existed — `ambiguous`, the
 * only answer the caller ever wanted here, stands.
 *
 * This is what makes a definitively failed challenge abandonable regardless of the SHAPE Stripe.js
 * used to report it — a resolved error of an unexpected `type`, or a thrown one.
 */
async function verdictFromRetrievedIntent(
  stripe: StripeInstance,
  clientSecret: string,
  onStage?: ScaStageReporter,
): Promise<NextActionVerdict> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const retrieved = await Promise.race([
      stripe.retrievePaymentIntent(clientSecret),
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), RETRIEVE_TIMEOUT_MS);
      }),
    ]);
    if (!retrieved) {
      report(onStage, 'intent-reread', 'timed-out');
      return 'ambiguous';
    }
    if (retrieved.error) {
      report(onStage, 'intent-reread', 'read-failed');
      return 'ambiguous';
    }
    const status = retrieved.paymentIntent?.status ?? '';
    report(onStage, 'intent-reread', status || 'none');
    return classifyIntentStatus(status);
  } catch {
    report(onStage, 'intent-reread', 'threw');
    return 'ambiguous';
  } finally {
    clearTimeout(timer);
  }
}

/**
 * How a held (UPGRADE_REQUIRES_ACTION) upgrade ultimately resolved, and — because the caller keys
 * money behavior off it — what each value REQUIRES of the caller's idempotency key:
 *
 * - `paid` — the challenge completed and the payment settled/settling: reflect success and RELEASE
 *   the key (the next switch is a new action).
 * - `canceled` — the challenge definitively failed AND the server CONFIRMED voiding the held
 *   invoice: tell the user nothing was charged and RELEASE the key (a fresh action is allowed).
 * - `ambiguous` — everything else, including a definitive challenge failure whose cancel could not
 *   be confirmed (the payment may have completed concurrently): KEEP the key, never say "payment
 *   failed", and poll — an exact-key retry or the paid webhook converges it.
 */
export type HeldUpgradeResolution = 'paid' | 'canceled' | 'ambiguous';

/**
 * The full decision ladder for a held upgrade, from client secret to resolution. Pure orchestration
 * over injectable steps so the money-relevant sequencing — abandon ONLY on a definitive failure,
 * report `canceled` ONLY on a server-confirmed void, degrade a missing secret to ambiguous rather
 * than guessing — is pinned by unit tests instead of living untested inside the picker component.
 */
export async function resolveHeldUpgrade(
  clientSecret: string | null | undefined,
  publishableKey: string | null | undefined,
  billingLane: BillingLane | null | undefined,
  deps: {
    confirm?: (
      clientSecret: string,
      publishableKey: string,
      onStage?: ScaStageReporter,
    ) => Promise<NextActionVerdict>;
    abandon?: (billingLane: BillingLane) => Promise<boolean>;
    onStage?: ScaStageReporter;
  } = {},
): Promise<HeldUpgradeResolution> {
  const confirm = deps.confirm ?? confirmHeldUpgrade;
  const abandon = deps.abandon ?? abandonHeldUpgrade;
  const { onStage } = deps;
  // Held but no secret to confirm with (should not happen): the hold exists server-side and its
  // outcome is unknowable from here — don't guess, don't abandon, resync.
  if (!clientSecret || !publishableKey || (billingLane !== 'live' && billingLane !== 'sandbox')) {
    // The one path that reaches NO Stripe call at all. Named separately from `stripe-unavailable`
    // because the cause is our own response shape, not the buyer's browser.
    report(
      onStage,
      'no-confirmable-response',
      !clientSecret ? 'no-secret' : !publishableKey ? 'no-key' : `bad-lane:${billingLane ?? 'none'}`,
    );
    report(onStage, 'verdict', 'ambiguous');
    return 'ambiguous';
  }
  const verdict = await confirm(clientSecret, publishableKey, onStage);
  report(onStage, 'verdict', verdict);
  if (verdict === 'paid') return 'paid';
  if (verdict === 'not_completed') {
    const canceled = await abandon(billingLane);
    report(onStage, 'abandon', canceled ? 'confirmed' : 'unconfirmed');
    return canceled ? 'canceled' : 'ambiguous';
  }
  // An ambiguous challenge outcome must never abandon the hold — the payment may have succeeded.
  return 'ambiguous';
}

// Same budget and the same reason as RETRIEVE_TIMEOUT_MS above: both callers run this inside a modal
// the picker holds non-dismissible while it works, so an unbounded wait renders as a hung dialog with
// no key and no button that escapes it. Past this budget we stop WAITING — we deliberately do not
// abort the request, because the server may be mid-void and cancelling that is worse than not hearing
// about it. Not hearing is `false`, which is already the answer for every other "we cannot confirm".
const ABANDON_TIMEOUT_MS = 8_000;

/**
 * Explicitly abandon a held upgrade so its invoice is voided now. Returns whether the server
 * CONFIRMED the abandonment.
 *
 * `false` is "unconfirmed", NOT "still pending", and it covers four different things: nothing was
 * pending; the deliberate fail-closed answer for "the payment may have completed concurrently"; the
 * service refusing to void a pending update whose kind it does not recognize (another flow's
 * invoice); and this call outrunning its budget. The caller cannot tell them apart, so it must not
 * promise the user the change was discarded — and must not tell them it went through either. Resync.
 */
export async function abandonHeldUpgrade(billingLane: BillingLane): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const res = await Promise.race([
      fetch(`${API_BASE}/api/premium/cancel-pending-upgrade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ billingLane }),
      }),
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), ABANDON_TIMEOUT_MS);
      }),
    ]);
    if (!res) return false;
    const json = (await res.json()) as { canceled?: boolean };
    return res.ok && json.canceled === true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}
